/**
 * The decision log (docs/01 §4.4).
 *
 * "Every resolved proposal is permanent and queryable: who proposed, who voted
 * which way, when, and why it resolved." That record is what stops the same
 * argument happening twice, so it is a first-class screen rather than an
 * admin page — but it is collapsed by default, because the answer people
 * usually want is just the outcome.
 *
 * The event list comes from `decisionLog`, which imposes a total order, so two
 * phones that synced in different orders render byte-identical histories.
 */

import { formatCairoTime12, rolloverDayOf, type MemberId } from '@office/domain'
import { queryDecisionLog, type DecisionLogEvent } from '@office/domain/proposals'
import { describeCounter, describeOutcome, type ProposalView } from './proposal-data.js'
import { nameOf, type MemberLite } from '../chores/live.js'
import { formatDay } from '../chores/chore-data.js'
import '../chores/phone.css'
import './proposals.css'

export interface DecisionLogProps {
  decided: readonly ProposalView[]
  log: readonly DecisionLogEvent[]
  members: readonly MemberLite[]
  me: MemberId
  /**
   * The configured office day boundary. Defaulting it here would date a 02:00
   * decision to the calendar day rather than the office day everyone was still
   * working through, and disagree with every other date on the phone.
   */
  rolloverHour: number
}

export function DecisionLog({ decided, log, members, me, rolloverHour }: DecisionLogProps) {
  return (
    <section className="ph-section" aria-labelledby="log-title">
      <div className="ph-section-head">
        <h2 id="log-title" className="ph-section-title">
          Decision log
        </h2>
        {decided.length > 0 ? <span className="ph-count">{decided.length}</span> : null}
      </div>
      {decided.length === 0 ? (
        <div className="ph-empty">
          <span className="ph-empty-mark" aria-hidden="true">
            🗂
          </span>
          <p className="ph-empty-line">Nothing has been decided yet.</p>
          <p className="ph-empty-hint">
            Every resolved proposal lands here for good — who proposed, who voted which way, and
            why it closed.
          </p>
        </div>
      ) : (
        <ul className="ph-list">
          {decided.map(v => (
            <li key={String(v.proposal.id)} className="card pr-decided" data-testid="decided">
              <details>
                <summary className="pr-summary">
                  <span className="pr-summary-title">{v.proposal.title}</span>
                  <span className="pr-summary-meta">
                    {describeOutcome(v.proposal.status, v.proposal.decisionReason)}
                    {v.proposal.decidedAt === null
                      ? ''
                      : ` · ${formatDay(
                          rolloverDayOf(v.proposal.decidedAt, rolloverHour),
                        )} ${formatCairoTime12(v.proposal.decidedAt)}`}
                  </span>
                </summary>
                <ol className="pr-events">
                  {queryDecisionLog(log, { proposalId: v.proposal.id }).map(e => (
                    <li key={e.seq} className="pr-event">
                      <span className="pr-event-time">{formatCairoTime12(e.at)}</span>
                      <span className="pr-event-body">{describeEvent(e, members, me)}</span>
                    </li>
                  ))}
                </ol>
              </details>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export function describeEvent(
  e: DecisionLogEvent,
  members: readonly MemberLite[],
  me: MemberId,
): string {
  const who = e.actorId === null ? null : e.actorId === me ? 'You' : nameOf(members, e.actorId)
  switch (e.kind) {
    case 'proposed':
      return `${who ?? 'Someone'} proposed it${e.comment === null ? '' : ` — ${e.comment}`}`
    case 'voted': {
      const verb = e.vote === 'approve' ? 'approved' : 'said no'
      const counter =
        e.counterProposal === null
          ? ''
          : ` — suggested ${describeCounter({
              proposalId: e.proposalId,
              voterId: e.actorId ?? ('' as MemberId),
              vote: e.vote ?? 'reject',
              payload: e.counterProposal,
              comment: e.comment,
              votedAt: e.at,
            })}`
      const comment = e.comment === null ? '' : ` — ${e.comment}`
      return `${who ?? 'Someone'} ${verb}${comment}${counter}`
    }
    case 'reminded':
      return 'Reminder sent to everyone who had not voted'
    case 'decided':
      return `${describeOutcome(e.status ?? 'pending', e.reason)}${
        who === null ? '' : ` (${who})`
      }`
  }
}
